import React, { useState } from 'react';
import { ArrowLeft, Heart, MapPin, Star, Crown, Trash2, Store, ShoppingBag, Calendar, BookOpen, Briefcase } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { OFFICES, SERVICES, PRODUCTS, COURSES } from '../data/database';
import { useI18n } from '../i18n';

const FavoritesPage = ({ onBack, onNavigate }) => {
  const { favorites, toggleFavorite } = useApp();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('offices');
  
  // Filter favorites by type
  // favorites structure: { businesses: [id], services: [id], products: [id], events: [id], courses: [id] }
  // For backwards compatibility, if favorites is an array, treat it as business IDs
  const getFavorites = () => {
    if (Array.isArray(favorites)) {
      return {
        offices: favorites,
        services: [],
        products: [],
        events: [],
        courses: []
      };
    }
    return favorites || { offices: [], services: [], products: [], events: [], courses: [] };
  };

  const favData = getFavorites();
  
  const favoriteOffices = OFFICES.filter(o => favData.offices?.includes(o.id));
  const favoriteServices = SERVICES.filter(s => favData.services?.includes(s.id));
  const favoriteProducts = PRODUCTS.filter(p => favData.products?.includes(p.id));
  const favoriteEvents = []; // Modulo eventi non implementato
  const favoriteCourses = COURSES.filter(c => favData.courses?.includes(c.id));

  const totalFavorites = favoriteOffices.length + favoriteServices.length + favoriteProducts.length + favoriteCourses.length;

  const tabs = [
    { id: 'offices', label: 'Attività', icon: Store, count: favoriteOffices.length },
    { id: 'services', label: 'Servizi', icon: Briefcase, count: favoriteServices.length },
    { id: 'products', label: 'Prodotti', icon: ShoppingBag, count: favoriteProducts.length },
    { id: 'courses', label: 'Corsi', icon: BookOpen, count: favoriteCourses.length },
  ];

  // Empty state
  if (totalFavorites === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-100 sticky top-0 z-20 lg:top-[73px]">
          <div className="max-w-screen-2xl mx-auto flex items-center gap-4 px-6 py-4">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full lg:hidden" data-testid="favorites-back-btn">
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{t('favorites', 'myFavorites')}</h1>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center p-8 mt-20" data-testid="favorites-empty">
          <div className="w-24 h-24 lg:w-32 lg:h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
            <Heart className="w-12 h-12 lg:w-16 lg:h-16 text-red-300" />
          </div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">{t('favorites', 'noFavorites')}</h2>
          <p className="text-gray-500 text-center mb-6 lg:text-lg">{t('favorites', 'noFavoritesYet')}</p>
          <button 
            onClick={() => onNavigate('search-results')}
            className="bg-emerald-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-emerald-600"
            data-testid="favorites-explore-btn"
          >
            {t('favorites', 'exploreOffices')}
          </button>
        </div>
      </div>
    );
  }

  // Render office card
  const OfficeCardComponent = ({ office }) => (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow" data-testid={`favorite-office-${office.id}`}>
      <div className="relative">
        <img 
          src={office.image} 
          alt={office.name}
          className="w-full h-40 object-cover cursor-pointer"
          onClick={() => onNavigate('office', { id: office.id })}
        />
        {office.isElit && (
          <div className="absolute top-3 left-3 flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            <Crown className="w-3 h-3" />
            Elit
          </div>
        )}
        <button 
          onClick={() => toggleFavorite(office.id, 'offices')}
          className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          data-testid={`remove-favorite-office-${office.id}`}
        >
          <Heart className="w-5 h-5 text-red-500 fill-red-500" />
        </button>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate cursor-pointer hover:text-emerald-600" onClick={() => onNavigate('office', { id: office.id })}>
              {office.name}
            </h3>
            <p className="text-sm text-gray-500">{office.category}</p>
          </div>
          <div className="flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-lg ml-2">
            <Star className="w-4 h-4 text-emerald-500 fill-emerald-500" />
            <span className="font-bold text-emerald-600 text-sm">{office.rating}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
          <MapPin className="w-4 h-4 text-gray-400" />
          <span className="truncate">{office.address}</span>
        </div>
        <button 
          onClick={() => onNavigate('office', { id: office.id })}
          className="w-full mt-4 bg-emerald-500 text-white py-3 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
        >
          Vedi dettagli
        </button>
      </div>
    </div>
  );

  // Render service card
  const ServiceCardComponent = ({ service }) => {
    const office = OFFICES.find(o => o.id === service.officeId);
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow" data-testid={`favorite-service-${service.id}`}>
        <div className="relative">
          <img 
            src={service.image || office?.image} 
            alt={service.name}
            className="w-full h-40 object-cover cursor-pointer"
            onClick={() => onNavigate('service-detail', { id: service.id })}
          />
          <button 
            onClick={() => toggleFavorite(service.id, 'services')}
            className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 truncate">{service.name}</h3>
          <p className="text-sm text-gray-500 mt-1">{business?.name}</p>
          <div className="flex items-center justify-between mt-3">
            <span className="text-lg font-bold text-emerald-600">€{service.price}</span>
            <span className="text-sm text-gray-500">{service.duration} min</span>
          </div>
          <button 
            onClick={() => onNavigate('service-detail', { id: service.id })}
            className="w-full mt-4 bg-emerald-500 text-white py-3 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
          >
            Prenota
          </button>
        </div>
      </div>
    );
  };

  // Render product card
  const ProductCardComponent = ({ product }) => {
    const office = OFFICES.find(o => o.id === product.officeId);
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow" data-testid={`favorite-product-${product.id}`}>
        <div className="relative">
          <img 
            src={product.image} 
            alt={product.name}
            className="w-full h-40 object-cover cursor-pointer"
            onClick={() => onNavigate('product-detail', { id: product.id })}
          />
          <button 
            onClick={() => toggleFavorite(product.id, 'products')}
            className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
          <p className="text-sm text-gray-500 mt-1">{office?.name}</p>
          <div className="flex items-center justify-between mt-3">
            <span className="text-lg font-bold text-emerald-600">€{product.price}</span>
            {product.stock > 0 ? (
              <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Disponibile</span>
            ) : (
              <span className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">Esaurito</span>
            )}
          </div>
          <button 
            onClick={() => onNavigate('product-detail', { id: product.id })}
            className="w-full mt-4 bg-emerald-500 text-white py-3 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
          >
            Acquista
          </button>
        </div>
      </div>
    );
  };

  // Render event card
  const EventCardComponent = ({ event }) => {
    const business = OFFICES.find(b => b.id === event.officeId);
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow" data-testid={`favorite-event-${event.id}`}>
        <div className="relative">
          <img 
            src={event.image} 
            alt={event.name}
            className="w-full h-40 object-cover cursor-pointer"
            onClick={() => onNavigate('event-detail', { id: event.id })}
          />
          <div className="absolute top-3 left-3 bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            Evento
          </div>
          <button 
            onClick={() => toggleFavorite(event.id, 'events')}
            className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 truncate">{event.name}</h3>
          <p className="text-sm text-gray-500 mt-1">{business?.name}</p>
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>{event.date} • {event.time}</span>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-lg font-bold text-emerald-600">€{event.price}</span>
            <span className="text-xs text-gray-500">{event.spotsLeft} posti rimasti</span>
          </div>
          <button 
            onClick={() => onNavigate('event-detail', { id: event.id })}
            className="w-full mt-4 bg-purple-500 text-white py-3 rounded-xl font-medium hover:bg-purple-600 transition-colors"
          >
            Partecipa
          </button>
        </div>
      </div>
    );
  };

  // Render course card
  const CourseCardComponent = ({ course }) => {
    const business = OFFICES.find(b => b.id === course.officeId);
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow" data-testid={`favorite-course-${course.id}`}>
        <div className="relative">
          <img 
            src={course.image} 
            alt={course.name}
            className="w-full h-40 object-cover cursor-pointer"
            onClick={() => onNavigate('course-detail', { id: course.id })}
          />
          <div className="absolute top-3 left-3 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            Corso
          </div>
          <button 
            onClick={() => toggleFavorite(course.id, 'courses')}
            className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          >
            <Heart className="w-5 h-5 text-red-500 fill-red-500" />
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 truncate">{course.name}</h3>
          <p className="text-sm text-gray-500 mt-1">{business?.name}</p>
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
            <BookOpen className="w-4 h-4" />
            <span>{course.duration} • {course.level}</span>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-lg font-bold text-emerald-600">€{course.price}</span>
          </div>
          <button 
            onClick={() => onNavigate('course-detail', { id: course.id })}
            className="w-full mt-4 bg-blue-500 text-white py-3 rounded-xl font-medium hover:bg-blue-600 transition-colors"
          >
            Iscriviti
          </button>
        </div>
      </div>
    );
  };

  // Get current items based on active tab
  const getCurrentItems = () => {
    switch (activeTab) {
      case 'offices':
        return favoriteOffices.map(o => <OfficeCardComponent key={o.id} office={o} />);
      case 'services':
        return favoriteServices.map(s => <ServiceCardComponent key={s.id} service={s} />);
      case 'products':
        return favoriteProducts.map(p => <ProductCardComponent key={p.id} product={p} />);
      case 'events':
        return favoriteEvents.map(e => <EventCardComponent key={e.id} event={e} />);
      case 'courses':
        return favoriteCourses.map(c => <CourseCardComponent key={c.id} course={c} />);
      default:
        return [];
    }
  };

  const currentItems = getCurrentItems();
  const currentTab = tabs.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20 lg:top-[73px]">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full lg:hidden" data-testid="favorites-back-btn">
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">{t('favorites', 'myFavorites')} ({totalFavorites})</h1>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex overflow-x-auto scrollbar-hide px-6 pb-3 gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    isActive 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                  {tab.count > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-6 py-6" data-testid="favorites-list">
        {currentItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
            {currentItems}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              {currentTab && <currentTab.icon className="w-10 h-10 text-gray-300" />}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun {currentTab?.label.toLowerCase()} nei preferiti</h3>
            <p className="text-gray-500 text-center">Esplora e aggiungi {currentTab?.label.toLowerCase()} ai tuoi preferiti</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
